"server-only";

import Head from "next/head";
import { serializeJsonForScriptTag } from "@/utility/html.utility";

interface HeadWithMetaTagsProps {
  /** The page title */
  title: string;

  /** The page's description */
  description: string;

  /** The preview thumbnail */
  imageUrl?: string;

  /** The canonical url of the page, e.g. https://thriftbooks.com/my-page/ */
  canonicalUrl: string;

  /** Optional JSON-LD structured data to render in a script tag */
  structuredData?: Record<string, unknown>;
}

/**
 * Renders the `<Head>` with meta tags
 * @param props The title, description, and other meta values.
 * @returns Returns a fragment containing a bunch of meta tags
 */
const HeadWithMetaTags: React.FC<
  HeadWithMetaTagsProps & React.PropsWithChildren
> = (props) => {
  const {
    title,
    description,
    canonicalUrl,
    imageUrl,
    structuredData,
    children,
  } = props;
  return (
    <Head>
      {/* HTML Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Facebook Meta Tags */}
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {imageUrl && <meta property="og:image" content={imageUrl} />}

      {/* Twitter Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta property="twitter:domain" content="thriftbooks.com" />
      <meta property="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {imageUrl && <meta name="twitter:image" content={imageUrl} />}

      {structuredData && (
        <script
          type="application/ld+json"
          // nosec
          dangerouslySetInnerHTML={{
            __html: serializeJsonForScriptTag(structuredData),
          }}
        />
      )}

      {children}
    </Head>
  );
};

export default HeadWithMetaTags;
